import {
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { switchMap } from 'rxjs/operators';
import swal from 'sweetalert2';
import { LoginService } from 'app/core/services/login.service';
import { UserService } from 'app/core/services/user.service';
import { AnalyticsService } from 'app/core/services/analytics.service';
import { User } from 'app/shared/models/user';
import { ActiveView } from '../../login-page.component';

const INVALID_LOGIN_MESSAGE_KEY = 'global.validate.user-login';
const ACCOUNT_NOT_ACTIVE_MESSAGE_KEY =
  'global.alert.auth-account-mail-attention';
// `info.reason` of the signin 400 for inactive accounts not scheduled for
// deletion (e.g. signups whose email was never confirmed).
const ACCOUNT_NOT_ACTIVE_REASON = 'ACCOUNT_NOT_ACTIVE';

/** Whether the signin failed because the account is not active. */
function isAccountNotActiveError(error: unknown): boolean {
  if (
    !(error instanceof HttpErrorResponse) ||
    error.status !== HttpStatusCode.BadRequest
  ) {
    return false;
  }
  // The signin request reads the response as text, so the body is a string.
  let body = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return false;
    }
  }
  return body?.info?.reason === ACCOUNT_NOT_ACTIVE_REASON;
}

@Component({
  standalone: true,
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
})
export class LoginFormComponent {
  @Output() changeView = new EventEmitter<ActiveView>();

  private fb = inject(FormBuilder);
  private loginService = inject(LoginService);
  private userService = inject(UserService);
  private analyticsService = inject(AnalyticsService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  isPasswordVisible = false;
  isLoading = false;

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  togglePassword(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const email = this.form.value.email!;
    const password = this.form.value.password!;

    this.loginService.login(email, password).subscribe({
      next: (data) => {
        if (!data.metadata?.active) {
          const deletionScheduledAt = (
            data.metadata as { deletionScheduledAt?: number }
          )?.deletionScheduledAt;
          if (deletionScheduledAt) {
            this.isLoading = false;
            this.promptReactivation(data, email);
            return;
          }

          this.showLoginError(ACCOUNT_NOT_ACTIVE_MESSAGE_KEY);
          this.isLoading = false;
          return;
        }

        this.analyticsService.submitUserLoginEvent({ id: email });

        this.loginService.saveSession(data);
        // Consent is reconciled from the backend in AppComponent once the
        // session is set (backend = cross-device source of truth).

        this.isLoading = false;
        this.router.navigate([''], { queryParams: { afterLogin: true } });
      },
      error: (error: unknown) => {
        let messageKey = INVALID_LOGIN_MESSAGE_KEY;
        if (isAccountNotActiveError(error)) {
          messageKey = ACCOUNT_NOT_ACTIVE_MESSAGE_KEY;
        }
        this.showLoginError(messageKey);
        this.isLoading = false;
      },
    });
  }

  private showLoginError(messageKey: string): void {
    this.translate
      .get(messageKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: string) => {
        this.toastr.error(res, '', {
          positionClass: 'toast-top-full-width',
        });
      });
  }

  private promptReactivation(data: User, email: string): void {
    const keys = [
      'reactivate-modal.title',
      'reactivate-modal.text',
      'reactivate-modal.confirm',
      'reactivate-modal.cancel',
    ];
    this.translate.get(keys).subscribe((t: Record<string, string>) => {
      swal
        .fire({
          title: t['reactivate-modal.title'],
          text: t['reactivate-modal.text'],
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: t['reactivate-modal.confirm'],
          cancelButtonText: t['reactivate-modal.cancel'],
          // Colors applied via global styles.scss (uses $agres-warning / $agres-primary).
          customClass: {
            icon: 'reactivate-modal-icon',
            confirmButton: 'reactivate-modal-confirm',
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.reactivateAndEnter(data, email);
          }
        });
    });
  }

  private reactivateAndEnter(data: User, email: string): void {
    this.isLoading = true;
    // Save the session so the reactivate request is authenticated, then refresh
    // the access token (now active:true) before entering the app.
    this.loginService.saveSession(data);
    this.userService
      .reactivateAccount()
      .pipe(switchMap(() => this.loginService.silentRefresh()))
      .subscribe({
        next: () => {
          this.analyticsService.submitUserLoginEvent({ id: email });
          this.isLoading = false;
          this.router.navigate([''], { queryParams: { afterLogin: true } });
        },
        error: () => {
          this.isLoading = false;
          this.translate.get('reactivate.error').subscribe((res: string) => {
            this.toastr.error(res, '', {
              positionClass: 'toast-top-full-width',
            });
          });
        },
      });
  }
}
